import React from 'react';

/**
 * Press page component
 * Displays press releases, news coverage, and media resources
 * 
 * @returns {JSX.Element} The rendered Press page
 */
const PressPage: React.FC = () => {
  try {
    // Log component rendering
    console.log('Rendering Press page');

    // Press releases data
    const pressReleases = [
      {
        id: 1,
        title: 'Raidu Launches Enterprise AI Security Platform',
        date: 'April 15, 2025',
        summary: 'Raidu announces the general availability of its comprehensive AI security and governance platform for enterprise customers.',
        link: '#',
      },
      {
        id: 2,
        title: 'Raidu Raises $30M Series A to Secure Enterprise AI',
        date: 'February 8, 2025',
        summary: 'Funding round led by top venture capital firms will accelerate product development and go-to-market efforts.',
        link: '#',
      },
      {
        id: 3,
        title: 'Raidu Partners with Leading Healthcare Organizations',
        date: 'January 22, 2025',
        summary: 'Strategic partnerships will help healthcare providers implement AI securely while maintaining patient privacy.',
        link: '#',
      },
    ];

    // Media coverage data
    const mediaCoverage = [
      {
        id: 1,
        title: 'How Raidu is Addressing the AI Security Gap',
        publication: 'TechCrunch',
        date: 'March 30, 2025',
        summary: 'An in-depth look at how Raidu is helping enterprises secure their AI systems against emerging threats.',
        link: '#',
      },
      {
        id: 2,
        title: 'The Future of AI Governance: Interview with Raidu CEO',
        publication: 'Forbes',
        date: 'March 12, 2025',
        summary: "Raidu's CEO discusses the challenges and opportunities in AI governance for enterprise organizations.",
        link: '#',
      },
      {
        id: 3,
        title: 'Raidu Named to "AI Companies to Watch" List',
        publication: 'Gartner',
        date: 'February 25, 2025',
        summary: 'Industry analyst firm recognizes Raidu as a leader in the emerging AI security and governance space.',
        link: '#',
      },
      {
        id: 4,
        title: 'How Financial Institutions Are Securing AI with Raidu',
        publication: 'Wall Street Journal',
        date: 'February 10, 2025',
        summary: 'Case study of how major banks are implementing Raidu to secure their AI operations.',
        link: '#',
      },
    ];

    return (
      <div className="bg-white">
        {/* Hero section */}
        <div className="relative bg-indigo-50">
          <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Press & Media
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600 max-w-3xl">
              Get the latest news about Raidu, press releases, media coverage, and resources for journalists.
            </p>
          </div>
        </div>

        {/* Media contact section */}
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:px-8">
          <div className="bg-indigo-600 rounded-xl shadow-xl overflow-hidden">
            <div className="px-6 py-12 sm:px-12 lg:px-16">
              <div className="lg:grid lg:grid-cols-2 lg:gap-8">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">Media Contact</h2>
                  <p className="mt-4 text-lg leading-6 text-indigo-100">
                    For press inquiries, interview requests, or additional information about Raidu.
                  </p>
                  <div className="mt-8">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-indigo-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="ml-3 text-base text-indigo-100">
                        <p>press@raidu.com</p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-indigo-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div className="ml-3 text-base text-indigo-100">
                        <p>+1 (555) 123-4567</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-10 lg:mt-0">
                  <h3 className="text-xl font-bold tracking-tight text-white">Download Media Kit</h3>
                  <p className="mt-4 text-lg leading-6 text-indigo-100">
                    Access our media kit with logos, executive headshots, product screenshots, and brand guidelines.
                  </p>
                  <div className="mt-8">
                    <button
                      className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
                      onClick={() => {
                        try {
                          console.log('User clicked Download Media Kit');
                        } catch (error) {
                          console.error('Error handling Download Media Kit click:', error);
                        }
                      }}
                    >
                      Download Media Kit
                      <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Press releases section */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Press Releases</h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              The latest announcements from Raidu.
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {pressReleases.map((release) => (
              <article key={release.id} className="flex flex-col items-start">
                <div className="flex items-center gap-x-4 text-xs">
                  <time dateTime={release.date} className="text-gray-500">
                    {release.date}
                  </time>
                  <span className="relative z-10 rounded-full bg-indigo-50 px-3 py-1.5 font-medium text-indigo-600">
                    Press Release
                  </span>
                </div>
                <div className="group relative">
                  <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-indigo-600">
                    <a 
                      href={release.link}
                      onClick={() => {
                        try {
                          console.log(`User clicked press release: ${release.title}`);
                        } catch (error) {
                          console.error(`Error handling press release click for ${release.title}:`, error);
                        }
                      }}
                    >
                      <span className="absolute inset-0" />
                      {release.title}
                    </a>
                  </h3>
                  <p className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">{release.summary}</p>
                </div>
                <div className="relative mt-8 flex items-center gap-x-4">
                  <img 
                    src="/logos/raidu_logo.png" 
                    alt="Raidu Logo" 
                    className="h-10 w-10 object-contain bg-gray-50 rounded-full"
                    onError={(e) => {
                      try {
                        console.error('Error loading Raidu logo', e.currentTarget.src);
                      } catch (error) {
                        console.error('Error in logo error handling:', error);
                      }
                    }}
                  />
                  <div className="text-sm leading-6">
                    <p className="font-semibold text-gray-900">
                      <span className="absolute inset-0" />
                      Raidu Communications Team
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Media coverage section */}
        <div className="bg-gray-50 py-16 sm:py-24 mt-16">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:mx-0">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Media Coverage</h2>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Recent articles and features about Raidu in the press.
              </p>
            </div>
            <div className="mx-auto mt-10 space-y-6">
              {mediaCoverage.map((article) => (
                <div 
                  key={article.id} 
                  className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-xl font-semibold text-gray-900">{article.title}</h3>
                    <div className="mt-2 sm:mt-0">
                      <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-700">
                        {article.publication}
                      </span>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">{article.date}</p>
                  <p className="mt-4 text-gray-600">{article.summary}</p>
                  <div className="mt-6">
                    <a
                      href={article.link}
                      className="text-indigo-600 font-medium flex items-center hover:text-indigo-500"
                      onClick={() => {
                        try {
                          console.log(`User clicked media article: ${article.title}`);
                        } catch (error) {
                          console.error(`Error handling media article click for ${article.title}:`, error);
                        }
                      }}
                    >
                      Read full article
                      <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors during rendering
    console.error('Error rendering Press page:', error);
    
    // Return error state
    return (
      <div className="py-12 px-6 bg-red-50 rounded-lg mx-auto max-w-7xl">
        <h2 className="text-2xl font-bold text-red-700 mb-4">Error Loading Press Page</h2>
        <p className="text-red-600">
          We encountered an issue while loading this page. Please try refreshing or contact support.
        </p>
      </div>
    );
  }
};

export default PressPage;
